<template>
    <v-container fluid>
        <v-card class="card-format">
            <div class="card-section-header px-6 py-4">
                <div class="d-flex align-center flex-wrap" style="gap:10px;">
                    <v-text-field placeholder="Search" v-model="search" dense outlined hide-details
                        prepend-inner-icon="mdi-magnify" clearable style="width:260px;" class="header-filter-input">
                    </v-text-field>
                    <v-select :items="cutoffStatusOptions" item-text="label" item-value="value"
                        placeholder="All Cutoffs" v-model="filter_cutoff_status" dense outlined hide-details
                        clearable style="width:160px;" class="header-filter-input">
                    </v-select>
                    <v-select :items="getEventTypes" item-text="name" item-value="name" placeholder="All Types"
                        v-model="filter_type" dense outlined hide-details clearable style="width:140px;"
                        class="header-filter-input">
                    </v-select>
                    <v-select :items="getLocation" placeholder="All Locations" v-model="filter_location"
                        item-text="name" item-value="id" dense outlined hide-details clearable
                        style="width:170px;" class="header-filter-input">
                    </v-select>
                    <v-autocomplete :items="contributorFilterOptions" :filter="filterContributorOptions"
                        placeholder="All Contributors" v-model="filter_contributor_id" item-text="name"
                        item-value="id" dense outlined hide-details clearable style="width:220px;"
                        class="header-filter-input">
                        <template v-slot:selection="{ item }">
                            <span>{{ item.name }}</span>
                        </template>
                        <template v-slot:item="{ item }">
                            <span>{{ item.name }}</span>
                            <v-chip x-small class="ml-2" color="#3988AA" text-color="white"
                                v-if="item.role">{{ item.role.name }}</v-chip>
                        </template>
                    </v-autocomplete>
                    <v-spacer></v-spacer>
                    <v-btn outlined depressed class="events-header-btn header-outline-btn" @click="fetchData()"
                        :loading="loading">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                </div>
                <div v-if="hasActiveFilters" class="header-clear-row">
                    <v-btn x-small text class="header-clear-btn" @click="clearFilters()">
                        <v-icon left x-small>mdi-close</v-icon>
                        Clear filters
                    </v-btn>
                </div>
            </div>
        </v-card>

        <v-skeleton-loader v-if="loading" type="card, card, card" class="mt-4"></v-skeleton-loader>

        <template v-else>
            <div v-if="eventGroups.length === 0" class="empty-state">
                <v-icon size="48" color="#B9C4CA">mdi-check-decagram-outline</v-icon>
                <div class="mt-3">No outstanding invitee deposits — nice work!</div>
            </div>

            <v-card v-for="group in eventGroups" :key="group.event.id" class="group-card mt-4">
                <div class="group-header pa-4">
                    <div class="d-flex justify-space-between align-start flex-wrap" style="gap:16px;">
                        <div class="d-flex flex-wrap" style="gap:28px;flex:1;min-width:0;">
                            <div class="info-item">
                                <div class="info-label">EVENT</div>
                                <div class="info-value">
                                    {{ group.event.name }}
                                    <br>
                                    <small style="color:#3988AA;">{{ group.event.reference_code }}</small>
                                </div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">STATUS</div>
                                <div class="info-value">
                                    <v-chip x-small :color="statusColor(group.event.event_status)" text-color="white"
                                        class="font-weight-600">{{ group.event.event_status }}</v-chip>
                                </div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">TYPE</div>
                                <div class="info-value">
                                    <v-chip x-small label v-if="group.event.lead.event_type"
                                        :color="eventTypeColor(group.event.lead.event_type.name)"
                                        :text-color="eventTypeTextColor(group.event.lead.event_type.name)">
                                        {{ group.event.lead.event_type.name }}
                                    </v-chip>
                                </div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">ORGANISER</div>
                                <div class="info-value">
                                    {{ group.event.lead.first_name }} {{ group.event.lead.last_name }}
                                    <br>
                                    <small style="color:#7C7C7C;">{{ group.event.lead.email }}</small>
                                </div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">EVENT DATE</div>
                                <div class="info-value">{{ $date(group.event.lead.start_date).format('DD/MM/YYYY') }}</div>
                            </div>
                            <div class="info-item" v-if="group.contributors.length">
                                <div class="info-label">CONTRIBUTORS</div>
                                <div class="info-value">
                                    <div v-for="contributor in group.contributors" :key="contributor.id">
                                        {{ contributor.user?.name }}
                                        <v-chip x-small class="ml-1" color="#3988AA" text-color="white"
                                            v-if="contributor.user?.role">{{ contributor.user.role.name }}</v-chip>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex align-center" style="gap:10px;">
                            <div class="cutoff-pill" :class="cutoffPillClass(group)">
                                <div class="cutoff-pill-date">{{ group.cutoffLabel }}</div>
                                <div class="cutoff-pill-due">{{ group.dueText }}</div>
                            </div>
                            <v-btn small dark color="#3988AA" class="custom-buttons" depressed
                                @click="manageEvent(group.event)">Manage</v-btn>
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="d-flex flex-wrap align-center" style="gap:28px;">
                        <div class="stat-item">
                            <div class="stat-value">{{ group.depositOrBetterCount }} / {{ group.totalInvitees }}</div>
                            <div class="stat-label">Paid Deposit</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-value">{{ group.fullyPaidCount }} / {{ group.totalInvitees }}</div>
                            <div class="stat-label">Fully Paid</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-value" style="color:#E74C3C;">{{ get_selected_company.currency }}{{ group.totalDue.toFixed(2) }}</div>
                            <div class="stat-label">Deposit Still Due</div>
                        </div>
                        <v-spacer></v-spacer>
                        <div class="cutoff-control">
                            <label>Extend cutoff by</label>
                            <v-text-field v-model="group.event.cutoff_extension" type="number" dense outlined
                                hide-details suffix="days" style="width:130px;"
                                @change="onExtensionChange(group.event)"></v-text-field>
                        </div>
                        <div class="cutoff-control">
                            <label>Pay deposit up to</label>
                            <v-menu v-model="date_menu_open[group.event.id]" :close-on-content-click="false"
                                min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field :value="group.cutoffISO ? $date(group.cutoffISO).format('DD/MM/YYYY') : ''"
                                        readonly dense outlined hide-details v-bind="attrs" v-on="on"
                                        append-icon="mdi-calendar" style="width:160px;"></v-text-field>
                                </template>
                                <v-date-picker :value="group.cutoffISO"
                                    @input="onDatePicked(group.event, $event)"></v-date-picker>
                            </v-menu>
                        </div>
                    </div>
                </div>

                <v-divider></v-divider>

                <v-simple-table class="invitee-table">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th>Invitee</th>
                                <th>Contact</th>
                                <th>Package</th>
                                <th class="text-right">Deposit Due</th>
                                <th class="text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="invitee in group.unpaidInvitees" :key="invitee.id">
                                <td>{{ invitee.name }}</td>
                                <td>
                                    {{ invitee.email }}
                                    <span v-if="invitee.mobile" style="color:#3988AA;"> &middot; {{ invitee.mobile }}</span>
                                </td>
                                <td>
                                    <span v-if="invitee.quote">{{ invitee.quote.name }}</span>
                                    <span v-else style="color:#B0B0B0;">—</span>
                                </td>
                                <td class="text-right font-weight-600">{{ get_selected_company.currency }}{{ depositAmount.toFixed(2) }}</td>
                                <td class="text-right">
                                    <v-btn small text color="#3988AA" class="custom-buttons"
                                        @click="openMarkPaidDialog(group.event, invitee)">Mark as Paid</v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-card>
        </template>

        <v-dialog v-model="mark_paid_dialog" max-width="450px" persistent>
            <v-card v-if="invitee_to_mark_paid">
                <v-card-title>
                    Mark {{ invitee_to_mark_paid.name }} as Paid
                </v-card-title>
                <v-card-text>
                    This marks {{ invitee_to_mark_paid.name }} as Paid in Full without recording any payment
                    amount — use this for comped/free guests. They'll no longer show as owing a deposit.
                </v-card-text>
                <v-card-actions class="custom-buttons">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeMarkPaidDialog()">Cancel</v-btn>
                    <v-btn dark color="#3988AA" :loading="marking_paid" @click="confirmMarkPaid()">Mark as Paid</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import pageBadgesMixin from "@/common/PageNameStore/pageBadgesMixin"
import { lookups, loadLookups } from "@/common/companyLookups"
import { detailsAssignedTo } from "@/utils/eventPayments"
import { eventStatusColor } from "@/utils/eventStatus"

// Mirrors the invitee deposit amount charged by the client app's online checkout
// (ggb_client_app/src/modules/authorization/authorization.ts) — used here to estimate
// the total deposit still outstanding for an event.
const DEPOSIT_AMOUNT = 50
// Deposit deadline formula must stay in sync with ggb_client_app's
// src/modules/payment/deposit_window.ts and ManageSettings.vue's depositCutoffDate.
const BASE_CUTOFF_DAYS = 21

export default {
    mixins: [pageBadgesMixin],
    data: () => ({
        loading: true,
        events: [],
        search: null,
        filter_type: null,
        filter_location: null,
        filter_contributor_id: null,
        filter_cutoff_status: null,
        date_menu_open: {},
        depositAmount: DEPOSIT_AMOUNT,
        cutoffStatusOptions: [
            { label: 'Overdue', value: 'overdue' },
            { label: 'Due within 7 days', value: 'due_soon' },
            { label: 'Upcoming', value: 'upcoming' },
        ],
        mark_paid_dialog: false,
        marking_paid: false,
        event_to_mark_paid: null,
        invitee_to_mark_paid: null,
    }),
    created() {
        this.setPageName('Invitee Deposits Due')
    },
    mounted() {
        this.fetchData()
        loadLookups('event_types', 'advisors', 'locations')
    },
    computed: {
        getLocation() {
            return lookups.locations
        },
        getEventTypes() {
            return lookups.event_types
        },
        getAdvisors() {
            return lookups.advisors
        },
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
        hasActiveFilters() {
            return !!(this.search || this.filter_type || this.filter_location || this.filter_contributor_id || this.filter_cutoff_status)
        },
        contributorFilterOptions() {
            return (this.getAdvisors || [])
                .filter(u => u.role?.id !== 1 && (u.access || []).some(a => a.company_id === this.get_selected_company.id))
                .sort((a, b) => (a.role?.id ?? Infinity) - (b.role?.id ?? Infinity))
        },
        totalUnpaidInvitees() {
            return this.eventGroups.reduce((sum, g) => sum + g.unpaidInvitees.length, 0)
        },
        pageBadges() {
            const events = this.eventGroups.length
            const unpaid = this.totalUnpaidInvitees
            return [
                `${events} event${events == 1 ? '' : 's'}`,
                `${unpaid} invitee${unpaid == 1 ? '' : 's'} unpaid`,
            ]
        },
        eventGroups() {
            const now = Date.now()
            const search = (this.search || '').toLowerCase().trim()

            const groups = this.events.map(event => {
                const unpaidInvitees = (event.invitees || []).filter(inv => this.getInviteeStatus(event, inv) === 'Unpaid')
                const depositOrBetterCount = (event.invitees || []).filter(inv => this.getInviteeStatus(event, inv) !== 'Unpaid').length
                const fullyPaidCount = (event.invitees || []).filter(inv => this.getInviteeStatus(event, inv) === 'Paid in Full').length
                const cutoffMs = this.computeCutoffMs(event)
                return {
                    event,
                    unpaidInvitees,
                    totalInvitees: (event.invitees || []).length,
                    depositOrBetterCount,
                    fullyPaidCount,
                    totalDue: unpaidInvitees.length * DEPOSIT_AMOUNT,
                    cutoffMs,
                    cutoffISO: cutoffMs ? this.$date(cutoffMs).format('YYYY-MM-DD') : null,
                    cutoffLabel: cutoffMs ? this.$date(cutoffMs).format('DD MMMM YYYY') : 'No cutoff set',
                    dueText: this.formatDueText(cutoffMs, now),
                    contributors: this.sortedContributors(event),
                }
            }).filter(g => g.unpaidInvitees.length > 0)

            const filtered = groups.filter(g => {
                const event = g.event
                if (this.filter_type && event.lead?.event_type?.name !== this.filter_type) return false
                if (this.filter_location && event.lead?.location?.id !== this.filter_location) return false
                if (this.filter_contributor_id && !(event.contributors || []).some(c => c.user_id === this.filter_contributor_id)) return false
                if (this.filter_cutoff_status) {
                    if (g.cutoffMs == null) return false
                    const diffDays = (g.cutoffMs - now) / 86400000
                    if (this.filter_cutoff_status === 'overdue' && diffDays >= 0) return false
                    if (this.filter_cutoff_status === 'due_soon' && (diffDays < 0 || diffDays > 7)) return false
                    if (this.filter_cutoff_status === 'upcoming' && diffDays <= 7) return false
                }
                if (search) {
                    const haystack = [
                        event.name, event.reference_code, event.lead?.first_name, event.lead?.last_name, event.lead?.email
                    ].filter(Boolean).join(' ').toLowerCase()
                    if (!haystack.includes(search)) return false
                }
                return true
            })

            filtered.sort((a, b) => {
                if (a.cutoffMs == null) return 1
                if (b.cutoffMs == null) return -1
                return a.cutoffMs - b.cutoffMs
            })

            return filtered
        },
    },
    methods: {
        ...mapActions({
            setPageName: 'pageNameStore/setPageNameAction',
        }),
        async fetchData() {
            this.loading = true
            try {
                const response = await this.$axios.get(`admin/events/get_invitee_deposits_due/${this.get_selected_company.id}`)
                const result = response.data
                this.events = result.response ? result.data : []
                const menuState = {}
                this.events.forEach(event => { menuState[event.id] = false })
                this.date_menu_open = menuState
            } catch (_) {
                this.events = []
            }
            this.loading = false
        },
        clearFilters() {
            this.search = null
            this.filter_type = null
            this.filter_location = null
            this.filter_contributor_id = null
            this.filter_cutoff_status = null
        },
        filterContributorOptions(item, queryText) {
            const query = queryText.toLowerCase()
            const name = (item.name || '').toLowerCase()
            const role = (item.role?.name || '').toLowerCase()
            return name.includes(query) || role.includes(query)
        },
        sortedContributors(event) {
            return [...(event.contributors || [])].sort((a, b) => (a.user?.role?.id ?? Infinity) - (b.user?.role?.id ?? Infinity))
        },
        eventTypeColor(name) {
            return name === 'Hen' ? 'pink lighten-4' : name === 'Birthday' ? 'green lighten-4' : name === 'Party' ? 'orange lighten-4' : 'blue lighten-4'
        },
        eventTypeTextColor(name) {
            return name === 'Hen' ? 'pink darken-3' : name === 'Birthday' ? 'green darken-3' : name === 'Party' ? 'orange darken-3' : 'blue darken-3'
        },
        statusColor(status) {
            return eventStatusColor(status)
        },
        cutoffPillClass(group) {
            if (group.cutoffMs == null) return 'cutoff-neutral'
            const diffDays = (group.cutoffMs - Date.now()) / 86400000
            if (diffDays < 0) return 'cutoff-overdue'
            if (diffDays <= 7) return 'cutoff-due-soon'
            return 'cutoff-upcoming'
        },
        formatDueText(cutoffMs, now) {
            if (cutoffMs == null) return 'No cutoff set yet'
            const diffDays = Math.round((cutoffMs - now) / 86400000)
            if (diffDays === 0) return 'Due today'
            if (diffDays > 0) return `Due in ${diffDays} day${diffDays == 1 ? '' : 's'}`
            const overdueDays = Math.abs(diffDays)
            return `Overdue by ${overdueDays} day${overdueDays == 1 ? '' : 's'}`
        },
        // Base deposit deadline before any admin extension: 21 days from the lead's
        // first recorded payment date. Kept identical to ManageSettings.vue's
        // depositCutoffDate and ggb_client_app's getDepositDeadlineMs.
        computeBaseMs(event) {
            const payments = event.lead?.payments || []
            const firstPayment = payments.find(p => p.payment_date)
            if (!firstPayment?.payment_date) return null
            const base = new Date(firstPayment.payment_date)
            base.setDate(base.getDate() + BASE_CUTOFF_DAYS)
            return base.getTime()
        },
        computeCutoffMs(event) {
            const baseMs = this.computeBaseMs(event)
            if (baseMs == null) return null
            const extension = parseInt(event.cutoff_extension ?? 0) || 0
            return baseMs + extension * 24 * 60 * 60 * 1000
        },
        getInviteeStatus(event, item) {
            // Matched by paid_to (who the money is for), whoever paid it. A Contribution
            // (type 6) assigned to this invitee counts the same as a Full Payment — money
            // has actually been earmarked for them, whatever type it was originally
            // collected under. A fully-covered invitee needs no deposit chasing, so this
            // also keeps them out of this page's unpaid list.
            const details = detailsAssignedTo(event, item.id)
            if (details.some(y => y.payment_type_id == 1 || y.payment_type_id == 6)) return 'Paid in Full'
            return details.some(y => y.payment_type_id == 2) ? 'Paid Deposit' : 'Unpaid'
        },
        async onExtensionChange(event) {
            const ext = parseInt(event.cutoff_extension)
            event.cutoff_extension = isNaN(ext) ? 0 : ext
            await this.saveCutoffExtension(event)
        },
        onDatePicked(event, newDateStr) {
            this.$set(this.date_menu_open, event.id, false)
            const baseMs = this.computeBaseMs(event)
            if (baseMs == null || !newDateStr) return
            const base = new Date(baseMs)
            const baseDayMs = new Date(base.getFullYear(), base.getMonth(), base.getDate()).getTime()
            const pickedDayMs = new Date(`${newDateStr}T00:00:00`).getTime()
            const extension = Math.round((pickedDayMs - baseDayMs) / 86400000)
            event.cutoff_extension = extension
            this.saveCutoffExtension(event)
        },
        async saveCutoffExtension(event) {
            try {
                const payload = {
                    company_id: this.get_selected_company.id,
                    id: event.id,
                    cutoff_extension: event.cutoff_extension,
                }
                const response = await this.$axios.post(`admin/events/update_event/${event.id}`, payload)
                if (response.data?.response == true) {
                    this.$toast.success('Deposit cutoff updated')
                } else {
                    this.$toast.error('Failed to update deposit cutoff')
                }
            } catch (error) {
                this.$toast.error('Error updating deposit cutoff')
                console.error(error)
            }
        },
        manageEvent(event) {
            this.$router.push({ name: 'ManageEvent', params: { event_id: event.id } })
        },
        openMarkPaidDialog(event, invitee) {
            this.event_to_mark_paid = event
            this.invitee_to_mark_paid = invitee
            this.mark_paid_dialog = true
        },
        closeMarkPaidDialog() {
            this.mark_paid_dialog = false
            this.event_to_mark_paid = null
            this.invitee_to_mark_paid = null
        },
        async confirmMarkPaid() {
            const event = this.event_to_mark_paid
            const invitee = this.invitee_to_mark_paid
            this.marking_paid = true
            try {
                const payload = {
                    event_id: event.id,
                    company_id: this.get_selected_company.id,
                    total_paid_amount: 0,
                    payment_date: this.$date().format('YYYY-MM-DD hh:mm:ss'),
                    reference_code: `Marked paid (no payment) - ${Date.now()}`,
                    event_name: event.name,
                    quote_id: 0,
                    paid_by: invitee.id,
                    payment_type_id: 1,
                    payment_gateway: 'Manual',
                }
                const response = await this.$axios.post(`admin/invitees/mark_invitee_as_paid/${invitee.id}`, payload)
                if (response.data?.response == true) {
                    invitee.payments = [...(invitee.payments || []), {
                        payment_collection: { status: 'complete' },
                        payment_details: [{ payment_type_id: 1, paid_to: invitee.id, is_charge: false, amount: 0 }],
                    }]
                    this.$toast.success(`${invitee.name} marked as paid`)
                    this.closeMarkPaidDialog()
                } else {
                    this.$toast.error('Failed to mark invitee as paid')
                }
            } catch (error) {
                this.$toast.error('Error marking invitee as paid')
                console.error(error)
            }
            this.marking_paid = false
        },
    },
}
</script>

<style lang="scss" scoped>
.card-format {
    border-radius: 10px !important;
    border: 1px solid #E9E9E9 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
}

.card-section-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.card-section-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
}

.header-filter-input ::v-deep .v-input__control {
    background: white;
    border-radius: 4px;
}

.header-clear-row {
    margin: 6px 0 -8px -8px;
}

.header-clear-btn {
    font-family: Manrope;
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: none;
    color: white !important;
}

.header-outline-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    color: white !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
}

.events-header-btn {
    font-family: Manrope;
    font-weight: 600 !important;
    font-size: 13px !important;
    text-transform: none;
    height: 36px !important;
    padding: 0 14px !important;
}

.empty-state {
    text-align: center;
    color: #7C7C7C;
    font-family: Manrope;
    padding: 60px 0;
}

.group-card {
    border-radius: 12px !important;
    overflow: hidden;
    border: 1px solid #E9E9E9 !important;
    box-shadow: 5px 5px 13px 0px rgba(240, 240, 240, 0.90), -5px -5px 10px 0px rgba(255, 255, 255, 0.90) !important;
    margin-bottom: 20px;
}

.group-header {
    background-color: #FAFAFA;
}

.info-item {
    min-width: 100px;
}

.info-label {
    font-size: 10px;
    font-weight: 700;
    color: #9E9E9E;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 2px;
}

.info-value {
    font-family: Manrope;
    font-size: 13px;
    font-weight: 600;
    color: #101010;
    line-height: 1.5;
}

.stat-item {
    text-align: left;
}

.stat-value {
    font-family: Manrope;
    font-size: 18px;
    font-weight: 700;
    color: #101010;
}

.stat-label {
    font-size: 11px;
    font-weight: 600;
    color: #9E9E9E;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.cutoff-control {
    display: flex;
    flex-direction: column;
    gap: 4px;

    label {
        font-size: 11px;
        font-weight: 600;
        color: #7C7C7C;
    }
}

.cutoff-pill {
    border-radius: 10px;
    padding: 8px 16px;
    text-align: right;
    min-width: 190px;
}

.cutoff-pill-date {
    font-family: Manrope;
    font-size: 13px;
    font-weight: 700;
}

.cutoff-pill-due {
    font-size: 12px;
    font-weight: 600;
}

.cutoff-overdue {
    background: #FDECEC;
    .cutoff-pill-date { color: #C0392B; }
    .cutoff-pill-due { color: #C0392B; }
}

.cutoff-due-soon {
    background: #FFF6E5;
    .cutoff-pill-date { color: #B9770E; }
    .cutoff-pill-due { color: #B9770E; }
}

.cutoff-upcoming {
    background: #E9F7EF;
    .cutoff-pill-date { color: #1E8449; }
    .cutoff-pill-due { color: #1E8449; }
}

.cutoff-neutral {
    background: #F0F0F0;
    .cutoff-pill-date { color: #7C7C7C; }
    .cutoff-pill-due { color: #7C7C7C; }
}

.invitee-table {
    ::v-deep th {
        font-family: Manrope;
        font-size: 11px !important;
        font-weight: 700 !important;
        color: #9E9E9E !important;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    ::v-deep td {
        font-family: Manrope;
        font-size: 13px;
    }
}

:deep(.custom-buttons span) {
    text-transform: none;
    font-family: Manrope;
    font-size: 13px !important;
    font-weight: 600 !important;
    box-shadow: none !important;
}
</style>
